import { ChevronRightIcon } from '@heroicons/react/20/solid';
import React, { KeyboardEvent, useCallback } from 'react';
import { SUBACCOUNT_PICKER } from '../../constants/featureFlags';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { useModalState } from '../../hooks/useModalState';
import { PanelModal, RenderPanelContent } from '../PanelModal/PanelModal';
import { getSettingsPanels, SettingsPanel, SettingsPanelId } from './PanelConfig';
import { useSettingsState } from './SettingsState';

const MainSettingsPanel: React.FC = () => {
  const pushPanel = useSettingsState((state) => state.pushPanel);
  const { value: subaccountPickerEnabled } = useFeatureFlag(SUBACCOUNT_PICKER);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLButtonElement>, panelId: string) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        pushPanel(panelId);
      }
    },
    [pushPanel]
  );

  const settingsPanels = getSettingsPanels(subaccountPickerEnabled);
  const visiblePanels = settingsPanels.filter((panel: SettingsPanel) => panel.showInMenu);

  return (
    <div className="space-y-px py-2" role="menu">
      {visiblePanels.map((panel: SettingsPanel) => {
        return (
          <button
            key={panel.id}
            onClick={() => pushPanel(panel.id as SettingsPanelId)}
            onKeyDown={(e) => handleKeyDown(e, panel.id)}
            className="flex w-full items-center justify-between py-2 text-left text-xs text-contentSecondary hover:bg-backgroundPrimary sm:px-5"
            role="menuitem"
            tabIndex={0}
            aria-haspopup="dialog"
          >
            <RenderPanelContent component={panel.buttonLabel ?? panel.titleLabel} />
            <ChevronRightIcon className="ml-2 size-4 shrink-0 text-contentPrimary" />
          </button>
        );
      })}
    </div>
  );
};

export const SettingsModal: React.FC = () => {
  const settingsModal = useModalState(({ settingsModal }) => settingsModal);
  const setSettingsModal = useModalState(({ setSettingsModal }) => setSettingsModal);
  const { value: subaccountPickerEnabled } = useFeatureFlag(SUBACCOUNT_PICKER);

  const panelStack = useSettingsState((state) => state.panelStack);
  const direction = useSettingsState((state) => state.direction);
  const popPanel = useSettingsState((state) => state.popPanel);
  const resetPanels = useSettingsState((state) => state.resetPanels);

  const navigateBack = useCallback(() => {
    popPanel();
  }, [popPanel]);

  const handleClose = useCallback(() => {
    setSettingsModal('closed');
    setTimeout(() => resetPanels(), 300);
  }, [setSettingsModal, resetPanels]);

  const currentPanelId = panelStack.at(-1) ?? 'settings';
  const settingsPanels = getSettingsPanels(subaccountPickerEnabled);
  const currentPanelConfig: SettingsPanel | undefined = currentPanelId
    ? settingsPanels.find((panel) => panel.id === currentPanelId)
    : undefined;

  return (
    <PanelModal
      panelId={currentPanelId}
      open={settingsModal !== 'closed'}
      onClose={handleClose}
      title={<RenderPanelContent component={currentPanelConfig?.titleLabel ?? 'Settings'} />}
      currentPanel={currentPanelConfig?.component.component ?? MainSettingsPanel}
      panelClassName={currentPanelConfig?.component.panelClassName}
      direction={direction}
      onNavigateBack={panelStack.length > 0 ? navigateBack : undefined}
    />
  );
};
